import { useEffect, useId, useState } from 'react'

import { CURRENT_CLIENT } from '@/constants'
import { useTenantLogoSrc, type TenantLogoConfig } from '@/hooks/use-tenant-logo-src'
import { cn } from '@/lib/utils'

/** Resolve header / brand navy for SVG (`--primary-dark` matches top chrome; falls back to `--primary`). */
function readHeaderBrandFlood(): string {
  if (typeof document === 'undefined') return '#0B5CFF'
  const root = document.documentElement
  const cs = getComputedStyle(root)
  const darkHex = cs.getPropertyValue('--primary-dark').trim()
  if (darkHex) return darkHex
  const prim = cs.getPropertyValue('--primary').trim()
  if (prim) return prim
  return '#0B5CFF'
}

/**
 * Dilutes tenant header blue (`--primary-dark`) for light-mode watermark: faint chrome tint on muted banners.
 * Probe resolves `color-mix` so `feFlood` gets computed `rgb()` even when the raw token is oklch/hex.
 */
function readLightWatermarkWash(brandOpaque: string): string {
  if (typeof document === 'undefined') return brandOpaque
  try {
    const el = document.createElement('span')
    el.style.cssText =
      'position:absolute;left:-9999px;top:0;width:1px;height:1px;overflow:hidden;visibility:hidden;pointer-events:none;color:color-mix(in oklch, ' +
      brandOpaque +
      ' 55%, transparent)'
    document.documentElement.appendChild(el)
    const washed = getComputedStyle(el).color.trim()
    el.remove()
    if (washed && washed !== '') return washed
  } catch {
    /* noop */
  }
  return brandOpaque
}

function useWatermarkTheme(): {
  darkDom: boolean
  /** Washed navy (header `--primary-dark` family) for light-mode SVG flood. */
  lightWatermarkFlood: string
} {
  const [darkDom, setDarkDom] = useState<boolean>(() =>
    typeof document !== 'undefined' ? document.documentElement.classList.contains('dark') : false,
  )
  const [lightWatermarkFlood, setLightWatermarkFlood] = useState(() =>
    readLightWatermarkWash(readHeaderBrandFlood()),
  )

  useEffect(() => {
    const root = document.documentElement
    const sync = () => {
      setDarkDom(root.classList.contains('dark'))
      setLightWatermarkFlood(readLightWatermarkWash(readHeaderBrandFlood()))
    }
    sync()
    const mo = new MutationObserver(sync)
    mo.observe(root, { attributes: true, attributeFilter: ['class', 'style'] })
    return () => mo.disconnect()
  }, [])

  return { darkDom, lightWatermarkFlood }
}

export interface TenantLogoWatermarkProps {
  /** Extra classes on the absolutely positioned watermark layer (`aria-hidden`). */
  className?: string
  /** Horizontal repeat period (SVG px). Smaller ⇒ tighter columns. Default 354 (~3× original 118px pitch). */
  patternSize?: number
  /** Vertical advance per motif row (`pattern` tile height). Default `patternSize * rowPitchRatio`. */
  rowPitch?: number
  /** Multiplier vs `patternSize` when `rowPitch` is omitted. Default ~0.57 — midway between snug (0.52) and airy (0.62). */
  rowPitchRatio?: number
  /** Second layer shifted by `(patternSize/2, rowPitch/2)` for brick / alternating columns. Default true. */
  staggerBrick?: boolean
  /** Extra scale around centre after translating the oversized tiling plane (`translate(-50%,-50%)`). Default 1.06. */
  coverScale?: number
  /** Rotate the whole tiling layer clockwise (°). Default 45. */
  rotationDeg?: number
  /** Inset factor for drawable area inside `(patternSize × rowPitch)`; logos scale with `meet` to that rectangle. Default 0.92 — tighter than 0.84 (~half prior extra margin). */
  logoCellFill?: number
  /** When set, opacity in **both** light and dark chrome. Overrides per-mode opacities. */
  logoOpacity?: number
  /** Fill strength in light chrome — washed header blue when not `.dark`. Default tuned for muted empty-banner backgrounds. */
  logoOpacityLight?: number
  /** Base opacity in `.dark` mode (raster tint). Default 0.12. */
  logoOpacityDark?: number
}

/** Brick-stagger row tiling (`meet`). Only the banner clip trims the angled field — no per-cell cropping. */

export function TenantLogoWatermark(props: TenantLogoWatermarkProps) {
  const {
    className,

    patternSize = 354,

    rowPitch: rowPitchProp,

    rowPitchRatio = 0.57,

    staggerBrick = true,

    coverScale = 1.06,

    rotationDeg = 45,

    logoCellFill = 0.92,

    logoOpacity,

    logoOpacityLight = 0.28,

    logoOpacityDark = 0.12,
  } = props

  const rowPitch = Math.max(8, rowPitchProp ?? Math.round(patternSize * rowPitchRatio))

  const patternRootId = useId().replace(/:/g, '')
  const patternId = `${patternRootId}-row`
  const filterLightId = `${patternId}-lightBrand`

  const cfg = CURRENT_CLIENT as TenantLogoConfig & { name?: string }
  const src = useTenantLogoSrc({
    logoPath: cfg.logoPath,
    logoPathLight: cfg.logoPathLight,
    logoPathDark: cfg.logoPathDark,
  })

  const { darkDom, lightWatermarkFlood } = useWatermarkTheme()

  const resolvedOpacity = logoOpacity ?? (darkDom ? logoOpacityDark : logoOpacityLight)

  const drawableW = Math.min(patternSize - 4, Math.max(1, patternSize * logoCellFill))
  const drawableH = Math.min(rowPitch - 4, Math.max(1, rowPitch * logoCellFill))
  const cellOffsetX = (patternSize - drawableW) / 2
  const cellOffsetY = (rowPitch - drawableH) / 2

  if (!src) return null

  const useLightBrandFill = !darkDom

  return (
    <span className={cn('pointer-events-none absolute inset-0 block overflow-hidden', className)} aria-hidden>
      {/*
        Wide strips + 45° rotation leave corner gaps if the tiling plane matches the banner box.
        Use a large centred square (>> strip height, ≥ banner width) so only the outer overflow clip trims the field.
      */}
      <svg
        className="pointer-events-none absolute block max-h-none max-w-none"
        xmlns="http://www.w3.org/2000/svg"
        style={{
          width: 'min(150vmax, min(260vw, 260vh))',
          height: 'min(150vmax, min(260vw, 260vh))',
          left: '50%',
          top: '50%',
          position: 'absolute',
          transform: `translate(-50%, -50%) rotate(${rotationDeg}deg) scale(${coverScale})`,
          transformOrigin: 'center center',
        }}
      >
        <defs>
          {useLightBrandFill ? (
            <filter id={filterLightId} colorInterpolationFilters="sRGB">
              {/* Washed navy (header `--primary-dark` family); motif alpha preserved */}
              <feFlood floodColor={lightWatermarkFlood} floodOpacity="1" result="brandPaint" />
              <feComposite in="brandPaint" in2="SourceGraphic" operator="in" />
            </filter>
          ) : null}
          <pattern id={patternId} width={patternSize} height={rowPitch} patternUnits="userSpaceOnUse">
            <image
              href={src}
              x={cellOffsetX}
              y={cellOffsetY}
              width={drawableW}
              height={drawableH}
              preserveAspectRatio="xMidYMid meet"
              opacity={resolvedOpacity}
              {...(useLightBrandFill ? { filter: `url(#${filterLightId})` } : {})}
            />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill={`url(#${patternId})`} />
        {staggerBrick ? (
          <rect width="100%" height="100%" fill={`url(#${patternId})`} transform={`translate(${patternSize / 2} ${rowPitch / 2})`} />
        ) : null}
      </svg>
    </span>
  )
}
